import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const { user, setUser, branding } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "", referral_code: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data);
      } else {
        const { data } = await api.post("/auth/register", {
          email: form.email,
          password: form.password,
          name: form.name,
          referral_code: form.referral_code || null,
        });
        setUser(data);
      }
      toast.success("Welcome to the club");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="phone-wrap">
      <div className="phone-frame flex flex-col">
        <div className="relative">
          <img src={fileUrl(branding?.login_hero)} alt="Alpha Tattoo Club" className="w-full h-64 object-cover border-b-2 border-black" />
          <div className="absolute inset-0 flex flex-col justify-end p-5 bg-gradient-to-t from-black/70 to-transparent">
            <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#C7A656]">Members Only · Est. 2026</span>
            <h1 className="font-display font-black text-4xl text-[#F4F0E6] leading-none uppercase">Alpha<br/>Tattoo Club</h1>
          </div>
        </div>

        <div className="p-6 flex-1">
          <div className="flex border-2 border-black rounded-md overflow-hidden mb-6">
            {["login", "register"].map((m) => (
              <button
                key={m}
                data-testid={`auth-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`flex-1 py-3 font-display font-bold uppercase tracking-wide text-sm ${mode === m ? "bg-[#8B1E28] text-[#F4F0E6]" : "bg-[#EAE4D3] text-[#121212]"}`}
              >
                {m === "login" ? "Login" : "Create Account"}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="flex flex-col gap-3">
            {mode === "register" && (
              <input className="atc-input" placeholder="Full name" value={form.name} onChange={set("name")} required data-testid="auth-name" />
            )}
            <input className="atc-input" type="email" placeholder="Email address" value={form.email} onChange={set("email")} required data-testid="auth-email" />
            <input className="atc-input" type="password" placeholder="Password" value={form.password} onChange={set("password")} required data-testid="auth-password" />
            {mode === "register" && (
              <input className="atc-input" placeholder="Referral code (optional)" value={form.referral_code} onChange={set("referral_code")} data-testid="auth-referral" />
            )}
            {error && <p className="text-[#8B1E28] text-sm font-bold" data-testid="auth-error">{error}</p>}
            <button type="submit" className="atc-btn btn-primary mt-1" disabled={busy} data-testid="auth-submit">
              {busy ? <Loader2 size={18} className="animate-spin" /> : null}
              {mode === "login" ? "Sign In" : "Join The Club"}
            </button>
          </form>

          {mode === "login" && (
            <button type="button" className="w-full text-center mt-3 font-mono2 text-xs underline text-[#554E40]" onClick={() => navigate("/forgot-password")} data-testid="auth-forgot-link">
              Forgot your password?
            </button>
          )}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-[2px] bg-black" />
            <span className="font-mono2 text-xs uppercase tracking-widest text-[#554E40]">or</span>
            <div className="flex-1 h-[2px] bg-black" />
          </div>

          <button className="atc-btn btn-gold w-full" onClick={googleLogin} data-testid="auth-google">
            Continue with Google
          </button>
          <p className="text-center text-xs text-[#554E40] mt-6 font-mono2 uppercase tracking-widest">
            Alpha Tattoo Club · Morristown, Tennessee
          </p>
        </div>
      </div>
    </div>
  );
}
